import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const defaultTheme = {
  headerBackground: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(17, 24, 39, 0.98) 100%)',
  frameBackground: `
    radial-gradient(120% 120% at 100% 0%, rgba(120, 119, 198, 0.08) 0%, rgba(120, 119, 198, 0) 46%),
    linear-gradient(180deg, rgba(12, 18, 32, 0.98) 0%, rgba(10, 15, 27, 0.98) 100%)
  `,
  bodyBackground: 'rgba(10, 15, 26, 0.98)',
  titleColor: 'rgba(248, 250, 252, 0.98)',
  titleFontSize: '1.25rem',
  titleFontFamily: 'inherit',
  closeColor: '',
}

const withFallbackTheme = (theme = {}) => {
  const merged = { ...defaultTheme, ...(theme || {}) }
  return {
    ...merged,
    closeColor: merged.closeColor || merged.titleColor || defaultTheme.titleColor,
  }
}

const helpProseSx = {
  px: 1.05,
  pt: 1.05,
  pb: 1.2,
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    color: 'rgba(248, 250, 252, 0.98)',
    marginTop: 0,
    marginBottom: '0.6rem',
    lineHeight: 1.2,
  },
  '& p': {
    marginTop: 0,
    marginBottom: '0.8rem',
    lineHeight: 1.55,
  },
  '& ul, & ol': {
    marginTop: 0,
    marginBottom: '0.9rem',
    paddingLeft: '1.35rem',
  },
  '& li + li': {
    marginTop: '0.45rem',
  },
  '& strong': {
    color: 'rgba(248, 250, 252, 0.98)',
  },
  '&::-webkit-scrollbar': {
    width: '0.5rem',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.04)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(148, 163, 184, 0.28)',
    borderRadius: '999px',
  },
}

const helpChromeCloseSelector = '[data-anear-help-close], .anear-help-chrome-close'

const AnearHelpDialog = ({ open, title, html, theme, onClose }) => {
  const resolvedTheme = withFallbackTheme(theme)
  const chromeFromHtml =
    typeof html === 'string' && /\bdata-anear-help-layout\b/i.test(html)

  /** Injected markup uses data-anear-help-close / .anear-help-chrome-close; use React onClick so
   *  we never miss attach (MUI Dialog can mount paper after first useEffect when ref was still null). */
  const handleChromeHelpClick = (event) => {
    if (!chromeFromHtml) return
    const t = event.target
    if (typeof t.closest !== 'function') return
    if (t.closest(helpChromeCloseSelector)) {
      event.preventDefault()
      event.stopPropagation()
      onClose()
    }
  }

  const dialogPaperMaxHeight = {
    xs: 'calc(100svh - 7.25rem)',
    sm: 'calc(100svh - 8rem)',
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby={chromeFromHtml ? undefined : 'anear-help-dialog-title'}
      aria-label={chromeFromHtml ? title || 'Help' : undefined}
      BackdropProps={{
        sx: {
          top: {
            xs: '4.5rem',
            sm: '4.75rem',
          },
          bottom: {
            xs: '0.9rem',
            sm: '1.1rem',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: {
            xs: 'calc(100% - 1rem)',
            sm: 'min(32rem, calc(100% - 2rem))',
          },
          maxWidth: '32rem',
          maxHeight: dialogPaperMaxHeight,
          m: 1,
          borderRadius: 1.5,
          overflow: 'hidden',
          ...(chromeFromHtml
            ? {
                p: 0,
                bgcolor: resolvedTheme.bodyBackground,
                backgroundImage: 'none',
                overflowY: 'hidden',
                overflowX: 'hidden',
              }
            : {}),
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      sx={{
        zIndex: 999999,
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          pt: {
            xs: '5.25rem',
            sm: '5.6rem',
          },
          pb: {
            xs: '1.1rem',
            sm: '1.35rem',
          },
        },
        ...(chromeFromHtml
          ? {
              '& .MuiDialog-paper': {
                overflowY: 'hidden !important',
                overflowX: 'hidden',
              },
              '& .MuiDialogContent-root': {
                overflowY: 'hidden !important',
                overflowX: 'hidden',
              },
            }
          : {}),
      }}
    >
      {!chromeFromHtml ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.25,
              px: 2,
              pt: 1.5,
              pb: 1,
              borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
              background: resolvedTheme.headerBackground,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.05,
                minWidth: 0,
                flex: '1 1 auto',
                pr: 0.5,
              }}
            >
              <Typography
                id="anear-help-dialog-title"
                component="h2"
                variant="h6"
                sx={{
                  color: resolvedTheme.titleColor,
                  fontWeight: 700,
                  fontSize: resolvedTheme.titleFontSize,
                  fontFamily: resolvedTheme.titleFontFamily,
                  lineHeight: 1.25,
                  minWidth: 0,
                }}
              >
                {title || 'Help'}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="Close help dialog"
              size="small"
              sx={{
                color: resolvedTheme.closeColor,
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <DialogContent
            dividers
            sx={{
              bgcolor: resolvedTheme.bodyBackground,
              color: 'rgba(226, 232, 240, 0.88)',
              px: 1.5,
              pt: 1.4,
              pb: 1.15,
              maxHeight: {
                xs: 'calc(100svh - 12.25rem)',
                sm: 'calc(100svh - 13rem)',
              },
              overflow: 'hidden',
              flex: '1 1 auto',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                borderRadius: 1.2,
                border: '1px solid rgba(148, 163, 184, 0.16)',
                background: resolvedTheme.frameBackground,
                boxShadow: `
                  inset 0 0 0 1px rgba(255, 255, 255, 0.02),
                  0 0.5rem 1.2rem rgba(0, 0, 0, 0.22)
                `,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  maxHeight: {
                    xs: 'calc(100svh - 15.25rem)',
                    sm: 'calc(100svh - 16rem)',
                  },
                  overflowY: 'auto',
                  ...helpProseSx,
                }}
                dangerouslySetInnerHTML={{ __html: html || '' }}
              />
              <Box
                aria-hidden="true"
                sx={{
                  height: '0.9rem',
                  borderTop: '1px solid rgba(148, 163, 184, 0.16)',
                  background: `
                    linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%),
                    radial-gradient(100% 160% at 50% 0%, rgba(120, 119, 198, 0.08) 0%, rgba(120, 119, 198, 0) 60%)
                  `,
                }}
              />
            </Box>
          </DialogContent>
        </>
      ) : (
        <Box
          sx={{
            bgcolor: resolvedTheme.bodyBackground,
            color: 'rgba(226, 232, 240, 0.88)',
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: {
              xs: 0.75,
              sm: 0.9,
            },
          }}
        >
          <Box
            onClick={handleChromeHelpClick}
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 1.2,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              background: resolvedTheme.frameBackground,
              boxShadow: `
                inset 0 0 0 1px rgba(255, 255, 255, 0.02),
                0 0.5rem 1.2rem rgba(0, 0, 0, 0.22)
              `,
              overflow: 'hidden',
              '& > [data-anear-help-layout], & > .anear-help-layout, & > .anear-help-chrome': {
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              },
              '& [data-anear-help-layout] > header, & .anear-help-layout > header, & .anear-help-chrome > header, & .anear-help-chrome-header': {
                flex: '0 0 auto',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              },
              '& [data-anear-help-layout] .anear-help-chrome-body, & .anear-help-layout .anear-help-chrome-body, & .anear-help-chrome-body': {
                ...helpProseSx,
                flex: '1 1 auto',
                minHeight: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              },
            }}
            dangerouslySetInnerHTML={{ __html: html || '' }}
          />
        </Box>
      )}
    </Dialog>
  )
}

AnearHelpDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  html: PropTypes.string,
  theme: PropTypes.shape({
    headerBackground: PropTypes.string,
    frameBackground: PropTypes.string,
    bodyBackground: PropTypes.string,
    titleColor: PropTypes.string,
    titleFontSize: PropTypes.string,
    titleFontFamily: PropTypes.string,
    closeColor: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
}

export default AnearHelpDialog
